/**
 * ============================================================================
 * ProgramPageConnectModalForm — Controller (client)
 * ============================================================================
 * Owns form state (react-hook-form), validation via explicit resolver,
 * program-page lead flow (lead-create-new only), lead-check debounce,
 * OTP orchestration, redirect loader / thankyou, InView force-close,
 * setIsNewSignInFlow, GA/WebEngage/Storage side effects.
 * No JSX — view consumes the returned ViewModel.
 *
 * Program page differs from Landing:
 *  - Endpoint: lead-create-new (NOT lp-new)
 *  - Portal: isProgramPage ? "Degree" : ""
 *  - Sequential counsellor (not Promise.all)
 *  - prospect-activity after lead parse
 *  - Intl early /thankyou when !isNewLeadForm
 * ============================================================================
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch, type Control } from "react-hook-form";
import { useLeadFormValidationReset } from "@/helpers/useLeadFormValidationReset";
import { usePathname, useSearchParams } from "next/navigation";
import { trackingDetails } from "@/api/getOtherFormDetails";
import { useOTPContext } from "@/components/sections/CommonModals/context";
import { pushToDataLayer, pushToDataLayerDeferred } from "@/helpers/gtag";
import {
  getProgramId,
  getSpecializationValue,
} from "../../LandingPageConnectModalForm/utils";
import { StorageService } from "@/helpers/storage.service";
import { getGlobalStateValue, updateStateSession } from "@/context/utils";
import { useIpStackDetails } from "@/helpers/useIpStackDetails";
import { setLeadFormInputValue } from "@/helpers/leadFormDegreeProgramFields";
import { fetchIpInfoData } from "@/context/ip.storage.utils";
import { useModalDispatch } from "@/context/ModalContext";
import { IModalActions } from "@/types/modal";
import { useInView } from "react-intersection-observer";
import {
  checkExistingLead,
  createLead,
  createProspectActivity,
} from "../service/programPageConnectModalForm.service";
import {
  NATIONAL_CODE,
  createProgramPageConnectModalFormResolver,
  regexForInputs,
  type ProgramPageConnectModalFormValues,
} from "../model/programPageConnectModalForm.types";
import { decryptionJiopc } from "@/components/molecules/CryptoJs/Helpers";
import { jwtDecode } from "jwt-decode";

const LEAD_CHECK_DEBOUNCE_MS = 2000;
const IP_INFO_DELAY_MS = 1000;
const SCHOLARSHIP_NOTIFICATION_PATH = "/lp-nw/scholarship-notification";

export interface UseProgramPageConnectModalFormControllerOptions {
  ProgramId?: any;
  associatedPrograms: Array<ProgramDetails>;
  isNewLeadForm?: boolean;
  NewBannerUI?: NewBannerUI;
  isMobile?: boolean;
  setLeadFailed?: (x: boolean) => void;
  isSeatsFilledEnabled: boolean;
  Numbers: number;
  isProgramPage?: boolean;
}

export interface ProgramPageConnectModalFormViewModel {
  control: Control<ProgramPageConnectModalFormValues>;
  errors: Partial<
    Record<keyof ProgramPageConnectModalFormValues, string | undefined>
  >;
  isSubmitting: boolean;
  isOTPLoading: boolean;
  isFormValid: boolean;
  isApplicationRedirectLoaderDisabled: boolean;
  isThankYouBannerHidden: boolean;
  loadingCount: number;
  message: string | null;
  isNewLeadForm: boolean;
  isNational: boolean;
  isCertification: boolean;
  btnColors: { bg?: string; color?: string };
  isSeatsFilledEnabled: boolean;
  Numbers: number;
  setRefs: (node: HTMLFormElement | null) => void;
  handleFieldChange: (
    fieldName: keyof ProgramPageConnectModalFormValues,
    value: string | boolean,
  ) => void;
  handlePhoneNumberChange: (value: string) => void;
  handleFieldBlur: (
    fieldName: string,
    value: string,
    error: string | undefined,
  ) => void;
  updateCountryCode: (value: string) => void;
  handlePreDegree: () => void;
  handleFormSubmit: (e: React.FormEvent) => void;
}

export function useProgramPageConnectModalFormController(
  options: UseProgramPageConnectModalFormControllerOptions,
): ProgramPageConnectModalFormViewModel {
  const {

    ProgramId,
    associatedPrograms = [],
    isNewLeadForm = false,
    NewBannerUI,
    isMobile,
    setLeadFailed,
    isSeatsFilledEnabled,
    Numbers,
    isProgramPage,
  } = options;

  const path = usePathname();
  const isAfricaPage = path == "/africa";
  const searchParams = useSearchParams();
  const amsTypeParam = searchParams.get("AMSType");
  const tokenParam = searchParams.get("token") ? decryptionJiopc(searchParams.get("token")) : "";

  const jiopcData = useMemo(() => {
    if (amsTypeParam !== "Jiopc" || !tokenParam) return null;
    try {
      const parts = tokenParam.split(".");
      if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return null;
      const payload = jwtDecode<{ userName?: string }>(tokenParam);
      if (typeof payload?.userName !== "string" || !payload.userName.trim()) return null;
      return { AMSType: amsTypeParam, userName: payload.userName.trim() };
    } catch {
      return null;
    }
  }, [amsTypeParam, tokenParam]);

  const { ipStackDetails, countryCode } = useIpStackDetails(isAfricaPage);

  const apiResponseURL = useRef<string | null>(null);
  const isFiredOnceIpinfo = useRef(false);
  const counterInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const formNodeRef = useRef<HTMLFormElement | null>(null);

  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitUIBusy, setIsSubmitUIBusy] = useState(false);
  const [isThankYouBannerHidden, setIsThankYouBannerHidden] = useState(true);
  const [loadingCount, setLoadingCount] = useState(3);
  const [
    isApplicationRedirectLoaderDisabled,
    setApplicationRedirectLoaderDisabled,
  ] = useState(true);

  const {
    handleSendOTP,
    isOTPLoading,
    setShowLoader,
    setLeadResponse,
    isHeaderHidden,
    setIsHeaderHidden,
    setIsNewSignInFlow,
    isOTPSent,
  } = useOTPContext();

  const modalDispatch = useModalDispatch();
  const isSessionModalActive = StorageService.get("timeConnectModal");

  const { ref: formRef, inView } = useInView();
  const setRefs = useCallback(
    (node: HTMLFormElement | null) => {
      formNodeRef.current = node;
      formRef(node);
    },
    [formRef],
  );

  const Custom10Value =
    associatedPrograms[0]?.attributes?.BasicDetails?.Custom10PgType;
  const programType =
    associatedPrograms[0]?.attributes?.BasicDetails?.ProgramType;
  const isCertification = programType === "Certification";

  const { CTABackgroundColorMobile, CTABackgroundColorDesktop, CTAColorMobile, CTAColorDesktop } =
    NewBannerUI || {};

  const btnColors = {
    bg: isMobile ? CTABackgroundColorMobile : CTABackgroundColorDesktop,
    color: isMobile ? CTAColorMobile : CTAColorDesktop,
  };

  const resolver = useMemo(
    () => createProgramPageConnectModalFormResolver(),
    [],
  );

  const defaultFormValues: ProgramPageConnectModalFormValues = {
    fullName: "",
    countryCode,
    phoneNumber: "",
    emailId: "",
    programId: ProgramId,
    isTermsAccepted: true,
  };

  const {
    control,
    setValue,
    getValues,
    clearErrors,
    reset,
    trigger,
    handleSubmit: rhfHandleSubmit,
    formState: { errors: rhfErrors, isSubmitting, isValid },
  } = useForm<ProgramPageConnectModalFormValues>({
    defaultValues: defaultFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver,
  });

  useLeadFormValidationReset({
    clearErrors,
    isOTPSent,
    isHeaderHidden,
  });

  // Narrow useWatch subscriptions for side-effect effects only. Field values
  // in the view come from per-field <Controller> render props.
  const watchedPhoneNumber = useWatch({ control, name: "phoneNumber" });
  const watchedCountryCode = useWatch({ control, name: "countryCode" });
  const watchedEmailId = useWatch({ control, name: "emailId" });
  const watchedFullName = useWatch({ control, name: "fullName" });

  const isLoading = isSubmitUIBusy || isSubmitting;
  const isNational = watchedCountryCode === NATIONAL_CODE;

  useEffect(() => {
    setValue("countryCode", countryCode, { shouldValidate: true });
  }, [ipStackDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isSessionModalActive) {
      modalDispatch({
        type: IModalActions.UpdateModal,
        data: {
          isModalForceClose: inView,
        },
      });
    }
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsNewSignInFlow(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (counterInterval.current) {
        clearInterval(counterInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      (watchedFullName || watchedEmailId || watchedPhoneNumber) &&
      !isFiredOnceIpinfo.current
    ) {
      setTimeout(fetchIpInfoData, IP_INFO_DELAY_MS);
      isFiredOnceIpinfo.current = true;
    }
  }, [watchedFullName, watchedEmailId, watchedPhoneNumber]);

  const leadCheckOnCompletion = useCallback(async () => {
    const currentValues = getValues();
    await checkExistingLead({
      phoneNumber: currentValues.phoneNumber,
      emailId: currentValues.emailId,
      fullName: currentValues.fullName,
      countryCode: currentValues.countryCode,
    });
  }, [getValues]);

  // Debounced phone lead-check (2s).
  useEffect(() => {
    let isPhoneValid = false;

    if (watchedCountryCode === NATIONAL_CODE) {
      isPhoneValid = regexForInputs.phoneNumberIndia.test(watchedPhoneNumber);
    } else {
      isPhoneValid = regexForInputs.phoneNumberIntl.test(watchedPhoneNumber);
    }

    if (!isPhoneValid) return undefined;

    const handler = setTimeout(() => {
      leadCheckOnCompletion();
    }, LEAD_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [watchedPhoneNumber, watchedCountryCode, leadCheckOnCompletion]);

  // Debounced email lead-check (2s).
  useEffect(() => {
    if (!regexForInputs.emailId.test(watchedEmailId)) return undefined;

    const handler = setTimeout(() => {
      leadCheckOnCompletion();
    }, LEAD_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [watchedEmailId, leadCheckOnCompletion]);

  const updateCountryCode = useCallback(
    (value: string) => {
      setValue("countryCode", value, { shouldValidate: true, shouldDirty: true });
      setValue("phoneNumber", "", { shouldDirty: true });
      clearErrors("phoneNumber");
    },
    [setValue, clearErrors],
  );

  const handleFieldChange = useCallback(
    (
      fieldName: keyof ProgramPageConnectModalFormValues,
      value: string | boolean,
    ) => {
      setLeadFormInputValue(setValue, trigger, fieldName, value as any);

      if (fieldName === "countryCode") {
        setLeadFormInputValue(setValue, trigger, "phoneNumber", getValues("phoneNumber"));
      }
    },
    [getValues, setValue, trigger],
  );

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      setLeadFormInputValue(setValue, trigger, "phoneNumber", value);
    },
    [setValue, trigger],
  );

  const handleFieldBlur = useCallback(
    (fieldName: string, value: string, error: string | undefined) => {
      pushToDataLayerDeferred({
        event: "form_filled",
        field_name: fieldName,
        option_selected: value || "NA",
        error_message: error || "NA",
        section_name: getGlobalStateValue("parentSection") || "NA",
        page_type: getGlobalStateValue("pageType"),
        page_url: window?.location?.href || "NA",
      });
    },
    [],
  );

  const handlePreDegree = useCallback(() => {
    const alreadySet = StorageService.get("timeConnectModal");
    if (!alreadySet) {
      StorageService.set("timeConnectModal", true);
    }
  }, []);

  const checkLeadFail = useCallback(
    (status: boolean) => {
      setLeadFailed?.(status);
    },
    [setLeadFailed],
  );

  const resetState = useCallback(() => {
    setIsHeaderHidden(false);
    setApplicationRedirectLoaderDisabled(true);
    reset({
      fullName: "",
      countryCode: "+" + ipStackDetails?.location?.calling_code || "91",
      phoneNumber: "",
      emailId: "",
      programId: ProgramId,
      isTermsAccepted: true,
    });
    setLoadingCount(3);
  }, [ProgramId, ipStackDetails, reset, setIsHeaderHidden]);

  const startLoader = useCallback(
    (_prospectId?: string) => {
      counterInterval.current = setInterval(() => {
        setLoadingCount((count) => {
          if (count < 1) {
            if (counterInterval.current) {
              clearInterval(counterInterval.current);
            }
            window.location.href = (apiResponseURL.current || "") + "&e=1";
            setTimeout(resetState, 3000);
            return 0;
          }
          return count - 1;
        });
      }, 1000);
    },
    [resetState],
  );

  const buildTrackingMeta = useCallback(() => {
    const tracking = trackingDetails();
    return {
      ...tracking,
      utm_medium: tracking?.utm_medium || "request_a_callback",
    };
  }, []);

  const handleLeadSubmit = useCallback(
    (payload: ProgramPageConnectModalFormValues) => {
      const form: Record<string, any> = {
        name: payload.fullName,
        phone: payload.phoneNumber,
        countryCode: payload.countryCode,
        email: payload.emailId,
        PROGRAM_ID: getProgramId({
          programId: ProgramId,
          associatedPrograms,
        }),
        Custom6: getSpecializationValue({ associatedPrograms }),
        Portal: isProgramPage ? "Degree" : "",
        consent: true,
      };

      if (payload.countryCode !== NATIONAL_CODE && !isNewLeadForm) {
        window.location.href = "/thankyou";
      }

      const meta = buildTrackingMeta();

      const verifyOtpCb = async (prospectId?: string) => {
        setShowLoader(false);
        if (window.location.pathname === "/lp-nw") {
          setApplicationRedirectLoaderDisabled(true);
        } else if (window.location.pathname === SCHOLARSHIP_NOTIFICATION_PATH) {
          setIsThankYouBannerHidden(false);
        } else if (isNewLeadForm && apiResponseURL.current) {
          setApplicationRedirectLoaderDisabled(false);
          startLoader(prospectId);
        } else {
          startLoader(prospectId);
          setApplicationRedirectLoaderDisabled(false);
        }
      };

      const cb = () =>
        new Promise((resolve, reject) => {
          setIsSubmitUIBusy(true);

          function getEvent(event: any) {
            StorageService.set("mFitlerKeyDetails", event.detail);
          }

          async function performApiCalls() {
            try {
              setIsSubmitUIBusy(true);
              let leadSquareResponse: any;
              try {
                const apiUrl = jiopcData
                  ? "/api/salesforce.com/jiopc-lead-create-new"
                  : "";
                const jiopcFields = jiopcData
                  ? { AMSType: jiopcData.AMSType, SubscriberID: jiopcData.userName }
                  : {};

                leadSquareResponse = await createLead({
                  apiUrl,
                  form,
                  meta,
                  formExtra: {
                    Custom5: "False",
                    Custom10: Custom10Value || "",
                    ...jiopcFields
                  },
                });

                if (leadSquareResponse?.response) {
                  createProspectActivity(leadSquareResponse.response);
                }

                if (leadSquareResponse?.success) {
                  const resp = leadSquareResponse?.response || {};

                  pushToDataLayer({
                    LSQ_ID: resp?.ProspectId,
                  });
                  pushToDataLayer({
                    event: "MFilter_OTP_Verified_New",
                    LSQ_ID: resp?.ProspectId,
                  });
                  document.addEventListener("mfilterit:botDetect", getEvent);

                  StorageService.set("LSQ_ID", resp?.ProspectId);
                  updateStateSession({
                    formSectionName: "Apply Now",
                    leadStartingCta: "NA",
                  });
                  pushToDataLayer({
                    event: "lead_submit",
                    cta_text: "Submit",
                    lead_starting_cta: "NA",
                    lead_starting_url: window.location.href || "NA",
                    lead_id: resp?.ProspectId || "NA",
                    section_name:
                      getGlobalStateValue("parentSection") || "NA",
                    status: "success",
                    error_message: "NA",
                    lead_type: "lead_submit_1",
                    page_type: getGlobalStateValue("pageType"),
                    page_url: window?.location?.href || "NA",
                  });

                  setTimeout(() => {
                    window?.webengage?.track("Lead Form Filled", {
                      "Student Nationality":
                        payload.countryCode == NATIONAL_CODE
                          ? "National"
                          : "International",
                      "First Name": payload.fullName,
                      "Phone Number":
                        payload.countryCode + payload.phoneNumber,
                      "Email ID": payload.emailId,
                      "Form Type": "Apply",
                      "Section Name":
                        getGlobalStateValue("parentSection") || "",
                      "Application URL": resp?.URL || "",
                    });

                    window?.webengage?.user?.login(resp?.ContactId);
                    window?.webengage?.user?.setAttribute(
                      "we_email",
                      payload.emailId,
                    );
                    window?.webengage?.user?.setAttribute(
                      "we_first_name",
                      payload.fullName,
                    );
                    window?.webengage?.user?.setAttribute(
                      "we_phone",
                      payload.countryCode + payload.phoneNumber,
                    );
                  }, 0);

                  apiResponseURL.current =
                    leadSquareResponse.response.URL || "";

                  if (
                    payload.countryCode !== NATIONAL_CODE &&
                    !isNewLeadForm
                  ) {
                    window.location.href = "/thankyou";
                    return;
                  } else if (
                    payload.countryCode !== NATIONAL_CODE &&
                    isNewLeadForm
                  ) {
                    // bye pass the OTP
                    verifyOtpCb();
                  }

                  if (typeof setLeadResponse === "function") {
                    setLeadResponse(resp);
                  }
                  setIsHeaderHidden(true);
                  resolve(resp);
                }
              } catch (error) {
                console.error("LeadSquare API call failed:", error);
                pushToDataLayer({
                  event: "lead_submit",
                  cta_text: "Submit",
                  lead_starting_cta: "NA",
                  lead_starting_url: window.location.href || "NA",
                  lead_id: "NA",
                  section_name:
                    getGlobalStateValue("parentSection") || "NA",
                  status: "fail",
                  error_message: "LeadSquare API call failed.",
                  lead_type: "lead_submit_1",
                  page_type: getGlobalStateValue("pageType"),
                  page_url: window?.location?.href || "NA",
                });
                setShowLoader(false);
                setIsHeaderHidden(true);
              }

              if (!leadSquareResponse?.success) {
                setShowLoader(false);
                setIsHeaderHidden(true);
              }
              resolve(leadSquareResponse?.response);
            } catch (error) {
              console.error("Error:", error);
              const errorMessage = "Error occurred.";
              pushToDataLayer({
                event: "lead_submit",
                cta_text: "Submit",
                lead_starting_cta: "NA",
                lead_starting_url: window.location.href || "NA",
                lead_id: "NA",
                section_name: getGlobalStateValue("parentSection") || "NA",
                status: "fail",
                error_message: errorMessage,
                lead_type: "lead_submit_1",
                page_type: getGlobalStateValue("pageType"),
                page_url: window?.location?.href || "NA",
              });
              setMessage(errorMessage);
              reject(error);
            } finally {
              setIsSubmitUIBusy(false);
            }
          }
          performApiCalls();
        });

      handleSendOTP(form, cb, null, verifyOtpCb, checkLeadFail);
    },
    [
      Custom10Value,
      ProgramId,
      associatedPrograms,
      buildTrackingMeta,
      checkLeadFail,
      handleSendOTP,
      isNewLeadForm,
      isProgramPage,
      setIsHeaderHidden,
      setLeadResponse,
      setShowLoader,
      startLoader,
    ],
  );

  // Paint-first busy + double rAF before validate (INP).
  const handleFormSubmit = useCallback(
    async (e: React.FormEvent) => {
      e?.preventDefault?.();
      if (isLoading || isOTPLoading) return;

      try {
        await import("@/helpers/safeSubmit").then((m) =>
          m.submitAfterValidation({
            rhfHandleSubmit,
            startBusy: () => setIsSubmitUIBusy(true),
            onValid: async (values) => {
              handleLeadSubmit(values);
            },
            onInvalid: () => setIsSubmitUIBusy(false),
            event: e,
          }),
        );
      } catch {
        setIsSubmitUIBusy(false);
      }
    },
    [handleLeadSubmit, isLoading, isOTPLoading, rhfHandleSubmit],
  );

  const errors = {
    fullName: rhfErrors.fullName?.message,
    countryCode: rhfErrors.countryCode?.message,
    phoneNumber: rhfErrors.phoneNumber?.message,
    emailId: rhfErrors.emailId?.message,
    isTermsAccepted: rhfErrors.isTermsAccepted?.message,
  };

  return {
    control,
    errors,
    isSubmitting: isLoading,
    isOTPLoading,
    isFormValid: isValid,
    isApplicationRedirectLoaderDisabled,
    isThankYouBannerHidden,
    loadingCount,
    message,
    isNewLeadForm,
    isNational,
    isCertification,
    btnColors,
    isSeatsFilledEnabled,
    Numbers,
    setRefs,
    handleFieldChange,
    handlePhoneNumberChange,
    handleFieldBlur,
    updateCountryCode,
    handlePreDegree,
    handleFormSubmit,
  };
}
